// ABOUTME: Main work screen for service desk with three-panel layout matching the design.
// ABOUTME: Left: ticket list with updates, Middle: chat thread, Right: ticket details panel.

import { Badge, Box, useTheme } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { useTranslation } from 'react-i18next';
import Subheading from "../../../../../components/theme/text/Subheading";
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import { tokens } from "../../../../../theme";
import { ServicedeskTicketCardWarehouse } from "../components/ServicedeskTicketCard";
import TicketDetailPanel from "../components/TicketDetailPanel";
import MAddSupportTicket from "../components/modals/MAddSupportTicket";
import { useNewSupportTicketPrefill } from "../../../../../utils/newSupportTicketPrefill";
import {
    useGetSupportTicketsUpdatesForMeQuery,
    useGetSupportTicketsForMeQuery,
    useGetSupportTicketsNeedsReviewForMeQuery
} from "../../../../../newapi/warehouse/supportTicketApiSlice";
import { Add } from "@mui/icons-material";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import Text from "../../../../../components/theme/text/Text";
import useDocumentOffsetTop from "../../../../../hooks/useDocumentOffsetTop";

const gridOptions = {
    export: true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

// Compact date + time label for ticket cards, e.g. "7 Jul 14:30".
const formatUpdatedAt = (value) =>
    new Date(value).toLocaleString([], {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });

const ServiceDeskWorkScreen = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isStuck, setIsStuck] = useState(false);
    const [addTicketOpen, setAddTicketOpen] = useState(false);
    // Arriving from a sidepanel "create support ticket" action opens the modal with that item linked.
    const { prefill: newTicketPrefill, clearPrefill: clearNewTicketPrefill } = useNewSupportTicketPrefill();
    useEffect(() => {
        if (newTicketPrefill) setAddTicketOpen(true);
    }, [newTicketPrefill]);
    const sentinelRef = useRef(null);
    // Real height of everything above the chat (header + KPI cards + tab bar).
    // The KPI cards grow taller on narrower viewports, so this cannot be a constant.
    const chatTopOffset = useDocumentOffsetTop(sentinelRef, 320);

    // Fetch tickets with updates - poll every 30 seconds
    const { data: updatesData } = useGetSupportTicketsUpdatesForMeQuery(
        undefined,
        { pollingInterval: 30000 }
    );
    // Paginated full ticket list (infinite scroll target)
    const [allTicketsPage, setAllTicketsPage] = useState(1);
    const [allTickets, setAllTickets] = useState([]);
    const { ref: allTicketsSentinelRef, inView: allTicketsSentinelInView } = useInView({
        threshold: 1.0,
    });
    const { data: ticketData, isFetching: ticketDataFetching } = useGetSupportTicketsForMeQuery({
        page: allTicketsPage,
        limit: 20
    });

    // Resolved tickets the customer replied to — needs agent review before they auto-close.
    const { data: needsReviewData } = useGetSupportTicketsNeedsReviewForMeQuery(undefined);
    const needsReviewTickets = needsReviewData?.data || [];

    // Merge incoming page: when page 1 refetches (e.g. after a create),
    // put its items at the top and drop the duplicates from the tail.
    useEffect(() => {
        if (!ticketData?.data) return;
        const incomingPage = ticketData?.pagination?.page ?? allTicketsPage;
        setAllTickets((prev) => {
            if (incomingPage === 1) {
                const incomingIds = new Set(ticketData.data.map((t) => t.id));
                const tail = prev.filter((t) => !incomingIds.has(t.id));
                return [...ticketData.data, ...tail];
            }
            const seen = new Set(prev.map((t) => t.id));
            const incoming = ticketData.data.filter((t) => !seen.has(t.id));
            return [...prev, ...incoming];
        });
    }, [ticketData, allTicketsPage]);

    // Bump page when the sentinel scrolls into view (and we're not already fetching)
    useEffect(() => {
        if (!allTicketsSentinelInView || ticketDataFetching) return;
        const totalPages = ticketData?.pagination?.totalPages;
        if (totalPages && allTicketsPage >= totalPages) return;
        setAllTicketsPage((prev) => prev + 1);
    }, [allTicketsSentinelInView, ticketDataFetching, ticketData, allTicketsPage]);

    // Get unread count for badge
    const unreadCount = updatesData?.pagination?.total || 0;

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            ([entry]) => setIsStuck(!entry.isIntersecting),
            { threshold: 0, rootMargin: '-72px 0px 0px 0px' }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
            styles: {
                main: {
                    overflow: 'visible',
                },
            },
        });
    }, []);


    return (
        <Box sx={{
            display: "grid",
            gridTemplateColumns: "420px 1fr",
            "@media screen and (max-width: 63.9375em)": {
                gridTemplateColumns: "1fr",
            },
        }}>
            {/* Left - Ticket List */}
            <Box sx={{
                borderRight:`2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                "@media screen and (max-width: 63.9375em)": {
                    maxHeight: 300,
                },
            }}>
                <Box sx={{ px: 4, pt: 4, pb: 1 }}>
                    <Shbutton
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        endIcon={<Add/>}
                        onClick={() => setAddTicketOpen(true)}
                        className="block"
                    >
                        {t("new_ticket")}
                    </Shbutton>
                </Box>

                {updatesData?.data.length > 0 &&
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                pt:3,
                                px: 4,
                                pb: 1,
                            }}
                        >
                            <Subheading>
                                {t("ticket_updates_for_you")}
                            </Subheading>
                            {unreadCount > 0 && (
                                <Badge
                                    badgeContent={unreadCount}
                                    color="error"
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            fontSize: 12,
                                            height: 20,
                                            minWidth: 20,
                                            borderRadius: 10,
                                        }
                                    }}
                                />
                            )}
                        </Box>
                        {/* ticket update list */}
                        <Box
                            sx={{
                                pb:allTickets?.length > 0 ? 3 : 0
                            }}
                        >
                            {updatesData?.data?.map((ticket) => (
                                <ServicedeskTicketCardWarehouse
                                    key={ticket.id}
                                    ticket={{
                                        id: ticket.id,
                                        ticketNumber: ticket.ticketNumber,
                                        title: ticket.title,
                                        type: ticket.type,
                                        priority: ticket.priority?.toLowerCase(),
                                        lastUpdateTime: formatUpdatedAt(ticket.updatedAt),
                                        description: ticket.description,
                                        relationName: ticket.relation?.name || 'Unknown',
                                        contactName: ticket.relation?.contactName || 'Unknown',
                                        updates: [] // TODO: Map actual updates from backend
                                    }}
                                    onClick={() => setSelectedTicket(ticket)}
                                    selected={selectedTicket?.id === ticket.id}
                                />
                            ))}
                        </Box>
                    </>
                }

                {needsReviewTickets.length > 0 &&
                    <>
                        <Box
                            sx={{
                                pt: updatesData?.data?.length > 0 ? 0 : 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 4,
                                pb: 1,
                            }}
                        >
                            <Subheading>
                                {t("ticket_needs_review")}
                            </Subheading>
                        </Box>
                        <Box sx={{ pb: allTickets?.length > 0 ? 3 : 0 }}>
                            {needsReviewTickets.map((ticket) => (
                                <ServicedeskTicketCardWarehouse
                                    key={"nr" + ticket.id}
                                    ticket={{
                                        id: ticket.id,
                                        ticketNumber: ticket.ticketNumber,
                                        title: ticket.title,
                                        type: ticket.type,
                                        priority: ticket.priority?.toLowerCase(),
                                        lastUpdateTime: formatUpdatedAt(ticket.updatedAt),
                                        description: ticket.description,
                                        relationName: ticket.relation?.name || 'Unknown',
                                        contactName: ticket.relation?.contactName || 'Unknown',
                                        updates: []
                                    }}
                                    onClick={() => setSelectedTicket(ticket)}
                                    selected={selectedTicket?.id === ticket.id}
                                />
                            ))}
                        </Box>
                    </>
                }

                {allTickets?.length > 0 &&
                    <>
                        {/* All tickets section header */}
                        <Box
                            sx={{
                                pt: (updatesData?.data?.length > 0 || needsReviewTickets.length > 0) ? 0 : 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 4,
                                pb: 1,
                            }}
                        >
                            <Subheading>
                                {t("all_tickets_for_you")}
                            </Subheading>
                        </Box>
                        {/* All tickets list with infinite scroll */}
                        <Box>
                            {allTickets.map((ticket) => (
                                <ServicedeskTicketCardWarehouse
                                    key={ticket.id}
                                    ticket={{
                                        id: ticket.id,
                                        ticketNumber: ticket.ticketNumber,
                                        title: ticket.title,
                                        type: ticket.type,
                                        priority: ticket.priority?.toLowerCase(),
                                        lastUpdateTime: formatUpdatedAt(ticket.updatedAt),
                                        description: ticket.description,
                                        relationName: ticket.relation?.name || 'Unknown',
                                        contactName: ticket.relation?.contactName || 'Unknown',
                                        updates: []
                                    }}
                                    onClick={() => setSelectedTicket(ticket)}
                                    selected={selectedTicket?.id === ticket.id}
                                />
                            ))}
                            <Box ref={allTicketsSentinelRef} sx={{ height: 1 }} />
                        </Box>
                    </>
                }
            </Box>

            {/* Main section wrapper - stretches to fill grid cell */}
            <Box sx={{alignSelf: 'stretch'}}>
                {/* Sentinel for stuck detection */}
                <Box ref={sentinelRef} sx={{ height: 0 }} />
                {/* Sticky container */}
                <Box
                    sx={{
                        display:'flex',

                        borderTopRightRadius:24,
                        position: "sticky",
                        top: 72,
                        height: isStuck ? "calc(100vh - 72px)" : `calc(100vh - ${chatTopOffset}px)`,
                        transition: "height 0.2s ease",
                        "@media screen and (max-width: 63.9375em)": {
                            position: "static",
                            height: "auto",
                            maxHeight: "none",
                            overflow: "visible",
                        },
                    }}
                >
                    {!selectedTicket ? (
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 6,
                                textAlign: "center",
                                color: colors.txt["secondary"],
                            }}
                        >
                            <Box
                                sx={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: "50%",
                                    background:
                                        theme.palette.mode === "dark"
                                            ? colors.grey[400]
                                            : colors.grey[100],
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: colors.txt["primary"],
                                    mb:2,
                                }}
                            >
                                <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 36 }} />
                            </Box>
                            <Subheading>{t("no_ticket_selected")}</Subheading>
                            <Text align="center" light sx={{ maxWidth: 360 }}>
                                {t("select_ticket_to_view")}
                            </Text>
                        </Box>
                    ) : (
                        <TicketDetailPanel ticketId={selectedTicket.id} />
                    )}
                </Box>
            </Box>

            <MAddSupportTicket
                open={addTicketOpen}
                handleClose={() => { setAddTicketOpen(false); clearNewTicketPrefill(); }}
                initialLinkedItems={newTicketPrefill?.linkedItems}
                initialRelationId={newTicketPrefill?.relationId}
            />
        </Box>
    );
};

export default ServiceDeskWorkScreen;
